import React, { useState, useEffect, useMemo } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PageHeader, { PageHeaderProps } from "./pageHeader/PageHeader";
import { CoreButton, CoreSpinner } from "@verity/components/core";
import { binSearch } from "../../utilities/timestampBinSearch";
import { TimestampsArr, VideoEvent } from "../../utilities/useTimestamps";
import {
	isValidEvent,
	processDisplay,
	processLink,
	processPicture,
	processKeypressData,
	processKeypressTime,
	EventBadge,
	FlagLevelBadge,
} from "../student-selection/videoplayer/Timestamps/TimestampLinks";
import { SessionInfo } from "../TestResultsWrapper";
import IdPersonSignaturePhoto from "./idPersonPhoto";
import AssessmentSetting from "./assessmentSetting";
import RegenerateIcon from "../../../images/regenerate.svg?react";

import "../TestResultsWrapper.css";
import FirstPage from "./firstPage";
import { SessionNote } from "../student-selection/videoplayer/ProctoringVideoPlayer";
import { formatEventTimestamp } from "../ProctoringResultsPage/ProctoringEventsTable";
import { ThemeWrapper } from "@verity/components";
import i18n from "../../../i18n";
import { SignatureType } from "@verity/database/prisma";

const THUMBNAIL_WIDTH = 200;
const PAGE_HEIGHT = 1187;
const ROW_HEIGHT = 70;
const THUMBNAIL_HEIGHT = 124;
const PAGE_MARGIN = 70;
const TITLE_HEIGHT = 80;

const PDFGenerator = ({ component }: { component: ReturnType<typeof Report> }) => {
	const [reportReady, setReportReady] = useState({
		roomScan: false,
		events: false,
		photo: !(
			component?.props?.idPhotoAvailable &&
			component?.props?.personPhotoAvailable &&
			component?.props?.signatureType !== null
		),
	});

	const [buttonText, setButtonText] = useState(
		i18n.t("result.components.resultsPage.proctoringResultsReport.generateReport"),
	);
	const [state, setState] = useState<"notGenerated" | "generating" | "downloadReady" | "downloading">("notGenerated");

	useEffect(() => {
		if (!(reportReady.events && reportReady.roomScan && reportReady.photo)) {
			setState((s) => (s === "notGenerated" ? "notGenerated" : "generating"));
		} else {
			setState((s) => (s === "generating" ? "downloadReady" : s));
		}
	}, [reportReady.events, reportReady.roomScan, reportReady.photo]);

	useEffect(() => {
		if (state === "notGenerated") {
			setButtonText(i18n.t("result.components.resultsPage.proctoringResultsReport.generateReport"));
		} else if (state === "generating") {
			setButtonText(i18n.t("result.components.resultsPage.proctoringResultsReport.generating"));
		} else if (state === "downloadReady") {
			setButtonText(i18n.t("result.components.resultsPage.proctoringResultsReport.downloadReport"));
		} else if (state === "downloading") {
			setButtonText(i18n.t("result.components.resultsPage.proctoringResultsReport.downloading"));
		}
	}, [state]);

	const rerenderComponent = async () => {
		if (state !== "notGenerated") {
			setState("notGenerated");
			setReportReady({
				roomScan: false,
				events: false,
				photo: !(
					component?.props?.idPhotoAvailable &&
					component?.props?.personPhotoAvailable &&
					component?.props?.signatureType !== null
				),
			});
			await new Promise((r) => setTimeout(r, 2));
		}
		setState("generating");
	};

	const handleButtonClick = async () => {
		if (state === "notGenerated") {
			setState("generating");
			return;
		}

		// Start downloading process only if the component has been rendered and the report is ready
		if (state === "downloadReady") {
			setState("downloading");

			const element = document.getElementById("component-to-pdf");
			if (element) {
				const canvas = await html2canvas(element, { useCORS: true });
				const imgData = canvas.toDataURL("image/png");

				const pdf = new jsPDF({
					orientation: "portrait",
					unit: "px",
					format: "letter",
					compress: false,
				});

				const imgProps = pdf.getImageProperties(imgData);
				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = pdf.internal.pageSize.getHeight();
				const pdfImageWidth = pdfWidth;
				const pdfImageHeight = Math.floor((imgProps.height * pdfImageWidth) / imgProps.width);
				let heightLeft = pdfImageHeight;
				let position = 0;
				let pageNum = 1;
				while (heightLeft > 0) {
					pdf.addImage(imgData, "PNG", 0, position, pdfImageWidth, pdfImageHeight);
					heightLeft -= pdf.internal.pageSize.getHeight();
					position -= pdf.internal.pageSize.getHeight();
					if (heightLeft > 0) {
						pdf.setFontSize(10); // Set the font size for the footer
						pdf.text(`${pageNum}`, pdfWidth - 10, pdfHeight - 10, { align: "right" });
						pageNum += 1;
					}
					if (heightLeft > ROW_HEIGHT) {
						if (position !== 0) {
							pdf.addPage();
						}
					}
				}

				// values for file name
				const fullName = component?.props?.userInfo?.full_name;
				const sessionNum = component?.props?.sessionNum;
				const allEvents = component?.props?.allEvents;
				const name = typeof fullName === "string" ? fullName.toLowerCase().replaceAll(" ", "-") : "test-taker";
				const attemptNum = "attempt-" + (typeof sessionNum === "string" ? sessionNum : "1");
				const startDate =
					Array.isArray(allEvents) && typeof allEvents?.[0] === "object"
						? new Date(
								(allEvents as { type?: string; name?: string; logged_time?: string }[])?.filter(
									(event) => event?.type == "session" && event?.name === "start proctoring",
								)?.[0]?.logged_time ?? new Date(),
							)
						: new Date();
				const date =
					startDate.getUTCFullYear().toString() +
					"-" +
					(startDate.getUTCMonth() + 1).toString().padStart(2, "0") +
					"-" +
					startDate.getUTCDate().toString().padStart(2, "0");
				pdf.save(name + "_" + attemptNum + "_" + date + ".pdf");
			}

			setState("downloadReady");
		}
	};

	// Enhance the component with reportReady and setReportReady props
	const enhancedComponent = React.cloneElement(component, { reportReady, setReportReady });

	return (
		<>
			{state !== "notGenerated" && (
				<div
					style={{
						width: "918px",
						left: "-1000000px",
						position: "absolute",
					}}
				>
					{enhancedComponent}
				</div>
			)}
			<CoreButton
				role={state === "downloadReady" ? "link" : undefined}
				disabled={state === "downloading" || state === "generating"}
				onClick={handleButtonClick}
				endIcon={
					state === "generating" ? (
						<CoreSpinner
							size={13}
							variant="determinate"
							value={Object.entries(reportReady).filter((x) => x[1]).length * 25 + 25}
						/>
					) : state === "downloading" ? (
						<CoreSpinner size={13} />
					) : undefined
				}
			>
				{buttonText}
			</CoreButton>
			{state === "downloadReady" && (
				<CoreButton
					onClick={rerenderComponent}
					style={{
						marginLeft: "8px",
						padding: "5.5px",
						minWidth: "24px",
					}}
					disabled={state !== "downloadReady"}
					aria-label={i18n.t("result.components.resultsPage.proctoringResultsReport.regenerateButtonAriaLabel")}
				>
					<RegenerateIcon width={18} height={18} />
				</CoreButton>
			)}
		</>
	);
};

interface ReportProps extends PageHeaderProps {
	selectedProctoringSession?: SessionInfo;
	allEvents: VideoEvent[];
	sessionNotes: SessionNote[];
	roomScanNotes: SessionNote[];
	webcamVidSources: Record<string, string>;
	roomScanVidSource: Record<string, string>;
	webcamTimestampsArr: TimestampsArr;
	roomScanTimestampsArr: TimestampsArr;
	duration: number | undefined;
	roomScanDuration: number | undefined;
	timeOffset: number;
	uuid: string | undefined;
	idPhotoAvailable: boolean;
	personPhotoAvailable: boolean;
	signatureType: SignatureType | null;
	reportReady?: number;
	setReportReady?: React.Dispatch<
		React.SetStateAction<{
			roomScan: boolean;
			events: boolean;
			photo: boolean;
		}>
	>;
}

const isShowThumbnailEvent = (event: VideoEvent) => {
	return event.type === "face detection" || event.type === "object detection" || event.type === "head movements";
};

const Report = (props: ReportProps) => {
	const sessionEvents = props.allEvents.filter(isValidEvent);
	const sessionNotes = useMemo(() => props.sessionNotes, []);
	const roomScanNotes = useMemo(() => props.roomScanNotes, []);

	const keypressEvents = props.allEvents
		.filter((event) => event.type === "keyboard" && event.name === "keypress")
		.map((event) => {
			return {
				time: event.time,
				value: processKeypressData(event.details.value),
			};
		});
	const [videoEventThumbnails, setVideoEventThumbnails] = useState(
		new Array<{ event: VideoEvent; url: string }>(props.allEvents.length),
	);

	const [noteThumbnails, setNoteThumbnails] = useState(
		new Array<{ time: number; url: string }>(props.sessionNotes.length),
	);

	const [roomScanNoteThumbnails, setRoomScanNoteThumbnails] = useState(
		new Array<{ time: number; url: string }>(props.roomScanNotes.length),
	);

	const waitForVideoLoad = (videoEl: HTMLVideoElement) => {
		return new Promise((resolve) => {
			videoEl.addEventListener("load", function loadListener() {
				videoEl.removeEventListener("load", loadListener);
				resolve(true);
			});
			setTimeout(() => resolve(false), 3000);
		});
	};
	const loadVideoAtTime = (videoEl: HTMLVideoElement, time: number) => {
		return new Promise((resolve, reject) => {
			videoEl.addEventListener("canplay", function canPlayListener() {
				videoEl.removeEventListener("canplay", canPlayListener);
				resolve(true);
			});
			videoEl.addEventListener("error", function errorListener() {
				videoEl.removeEventListener("error", errorListener);
				reject();
			});
			setTimeout(() => resolve(false), 3000);

			videoEl.currentTime = time;
		});
	};

	async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
		return new Promise((resolve, reject) => {
			canvas.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				}
				reject(blob);
			});
		});
	}

	const loadRoomScanThumbnails = async (videoSources: Record<string, string>) => {
		let currentSource = 0;

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = THUMBNAIL_WIDTH;
		hiddenVideo.height = THUMBNAIL_HEIGHT;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;
		hiddenVideo.crossOrigin = "anonymous";
		await waitForVideoLoad(hiddenVideo);

		const thumbnailsCopy = [...roomScanNoteThumbnails];
		let index = 0;
		for (const note of props.roomScanNotes) {
			const time = note.time;
			const canvas = document.createElement("canvas");
			canvas.width = hiddenVideo.videoWidth;
			canvas.height = hiddenVideo.videoHeight;
			const context = canvas.getContext("2d");

			// get webcam video for given time
			const webcamTimestamp = props.roomScanTimestampsArr[binSearch(props.roomScanTimestampsArr, time)];
			if (currentSource < webcamTimestamp.vid) {
				// update hidden video source to current webcam video
				currentSource = webcamTimestamp.vid;
				hiddenVideo.src = `${videoSources?.[currentSource]}`;
			}
			await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0)))
				.then(() => {
					if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
						context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
					}
				})
				.catch(() => {
					console.warn("Thumbnail video could not load.");
				});
			thumbnailsCopy[index] = {
				time: time,
				url: URL.createObjectURL(await canvasToBlob(canvas)), // do not use canvas.toDataURL("image/png") for browser memory
			};
			index += 1;
		}
		setRoomScanNoteThumbnails(thumbnailsCopy);
	};

	const loadThumbnails = async (videoSources: Record<string, string>, type: "event" | "note") => {
		let currentSource = 0;

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = THUMBNAIL_WIDTH;
		hiddenVideo.height = THUMBNAIL_HEIGHT;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;
		hiddenVideo.crossOrigin = "anonymous";

		await waitForVideoLoad(hiddenVideo);

		if (props.duration) {
			let idx = 0;
			if (type === "event") {
				// process Video Events
				const eventThumbnailsCopy = [...videoEventThumbnails];
				for (const event of sessionEvents) {
					if (!isShowThumbnailEvent(event)) {
						idx += 1;
						continue;
					}
					const time = event.time;
					const canvas = document.createElement("canvas");
					canvas.height = hiddenVideo.videoHeight;
					canvas.width = hiddenVideo.videoWidth;
					const context = canvas.getContext("2d");

					// get webcam video for given time
					const webcamTimestamp = props.webcamTimestampsArr[binSearch(props.webcamTimestampsArr, time)];
					if (currentSource < webcamTimestamp.vid) {
						// update hidden video source to current webcam video
						currentSource = webcamTimestamp.vid;
						hiddenVideo.src = `${videoSources?.[currentSource]}`;
					}
					await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0))).catch(() => {
						console.warn("Thumbnail video could not load.");
					});
					if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
						context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
					}
					eventThumbnailsCopy[idx] = {
						event: event,
						url: URL.createObjectURL(await canvasToBlob(canvas)), // do not use canvas.toDataURL("image/png") for browser memory
					};
					idx += 1;
				}
				setVideoEventThumbnails(eventThumbnailsCopy);
			} else {
				idx = 0;
				const noteThumbnailsCopy = [...noteThumbnails];
				for (const note of props.sessionNotes) {
					const time = note.time;
					const canvas = document.createElement("canvas");
					canvas.height = hiddenVideo.videoHeight;
					canvas.width = hiddenVideo.videoWidth;
					const context = canvas.getContext("2d");

					// get webcam video for given time
					const webcamTimestamp = props.webcamTimestampsArr[binSearch(props.webcamTimestampsArr, time)];
					if (currentSource < webcamTimestamp.vid) {
						// update hidden video source to current webcam video
						currentSource = webcamTimestamp.vid;
						hiddenVideo.src = `${videoSources?.[currentSource]}`;
					}
					await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0))).catch(() => {
						console.warn("Thumbnail video could not load.");
					});
					if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
						context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
					}
					noteThumbnailsCopy[idx] = {
						time: time,
						url: URL.createObjectURL(await canvasToBlob(canvas)), // do not use canvas.toDataURL("image/png") for browser memory
					};
					idx += 1;
				}
				setNoteThumbnails(noteThumbnailsCopy);
			}
		}
	};

	const webcamVidSources = props.webcamVidSources;
	const roomScanVidSource = props.roomScanVidSource;
	useEffect(() => {
		if (!webcamVidSources) return;
		void (async () => {
			props.setReportReady?.((report) => ({ ...report, events: false }));
			try {
				await loadThumbnails(props.webcamVidSources, "event");
				await loadThumbnails(props.webcamVidSources, "note");
			} catch (e) {
				console.error(e);
			}
			setTimeout(() => {
				props.setReportReady?.((report) => ({ ...report, events: true }));
			}, 1000);
		})();
	}, [webcamVidSources, sessionNotes]);

	useEffect(() => {
		if (!webcamVidSources) return;
		void (async () => {
			props.setReportReady?.((report) => ({ ...report, roomScan: false }));
			await loadRoomScanThumbnails(props.roomScanVidSource);
			setTimeout(() => {
				props.setReportReady?.((report) => ({ ...report, roomScan: true }));
			}, 1000);
		})();
	}, [roomScanVidSource, roomScanNotes]);

	const renderEventCluster = (includeTitle: boolean, start: number, end: number, marginBottom: number) => {
		return (
			<div
				style={{
					borderRadius: "10px",
					border: "1px solid #ddd",
					marginBottom: `${marginBottom}px`,
					marginTop: `${includeTitle ? 0 : PAGE_MARGIN}px`,
				}}
			>
				<table
					style={{
						width: "100%",
						textAlign: "left",
						borderSpacing: "0px",
					}}
				>
					{includeTitle && (
						<thead>
							<tr style={{ height: `${ROW_HEIGHT}px`, fontSize: "18px" }}>
								<th
									style={{
										padding: "8px",
										width: "20%",
										textAlign: "center",
									}}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsReport.thumbnail")}
								</th>
								<th
									style={{
										padding: "8px",
										width: "50%",
									}}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsReport.event")}
								</th>
								<th
									style={{
										padding: "8px",
										width: "10%",
									}}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsReport.time")}
								</th>
							</tr>
						</thead>
					)}
					<tbody>
						{sessionEvents.slice(start, end).map((event, index) => {
							index += start;
							const link = processLink(event);
							return (
								<tr key={index} className="flag_desc_details" style={{ height: `${ROW_HEIGHT}px` }}>
									{videoEventThumbnails?.[index]?.url ? (
										<td
											style={{
												borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
												padding: "8px",
												textAlign: "center",
												width: "20%",
											}}
										>
											<img
												style={{ width: "160px", height: "102px", borderRadius: "4px" }}
												src={videoEventThumbnails?.[index]?.url}
												alt=""
											></img>
										</td>
									) : (
										<td
											style={{
												borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
												padding: "8px",
												textAlign: "center",
												width: "20%",
											}}
										>
											{(() => {
												const eventIcon = processPicture(event);
												return eventIcon
													? eventIcon({
															style: {
																width: "30px",
																height: "30px",
															},
														})
													: null;
											})()}
										</td>
									)}
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											width: "50%",
											textAlign: "left",
											padding: "0 12px",
										}}
									>
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												gap: 10,
												padding: "15px 0",
											}}
										>
											<div>
												<div
													style={{
														display: "inline-flex",
														alignItems: "center",
														gap: 3,
														marginRight: 7,
													}}
												>
													<FlagLevelBadge flagLevel={event.flag_level} />
													{event.resolved && (
														<EventBadge chipColor="green">
															{i18n.t("result.components.resultsPage.proctoringResultsReport.resolved")}
														</EventBadge>
													)}
												</div>
												{processDisplay(event, false)}
											</div>
											{link && (
												<p style={{ color: "blue", fontSize: "14px", margin: "2px" }}>
													{link.length > 50 ? link.slice(0, 50) + "..." : link}
												</p>
											)}
											{event.comment && (
												<div>
													<span style={{ fontWeight: 600 }}>
														{i18n.t("result.components.resultsPage.proctoringResultsReport.comment")}:
													</span>{" "}
													{event.comment}
												</div>
											)}
										</div>
									</td>
									<td
										className="flag_desc_details"
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											width: "10%",
										}}
									>
										{formatEventTimestamp(Math.max(0, event.time * 1000 - props.timeOffset), props.duration)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	};

	function renderEvents() {
		let includeTitle = true;
		let currentEventIndex = 0;
		const renderByPages = () => {
			const res = [];
			while (currentEventIndex < sessionEvents.length) {
				const heightLeft = PAGE_HEIGHT - PAGE_MARGIN * 2;
				let currentHeight = 0;
				if (includeTitle) currentHeight += ROW_HEIGHT + TITLE_HEIGHT; // title height
				let rows = 0;
				while (currentEventIndex + rows < sessionEvents.length) {
					const rowHeight = isShowThumbnailEvent(sessionEvents[currentEventIndex + rows])
						? THUMBNAIL_HEIGHT
						: ROW_HEIGHT;
					if (currentHeight + rowHeight > heightLeft) {
						break;
					} else {
						rows += 1;
						currentHeight += rowHeight;
					}
				}
				const marginBottom = PAGE_MARGIN + heightLeft - currentHeight;
				const eventCluster = renderEventCluster(
					includeTitle,
					currentEventIndex,
					currentEventIndex + rows,
					marginBottom,
				);
				currentEventIndex += rows;
				includeTitle = false;
				res.push(eventCluster);
			}
			return res;
		};

		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					borderRadius: "10px",
					minHeight: "150px",
					marginLeft: `40px`,
					marginRight: `40px`,
					marginTop: `${PAGE_MARGIN}px`,
				}}
			>
				<div style={{ height: TITLE_HEIGHT, display: "flex", flexDirection: "column", gap: 5, paddingBottom: 20 }}>
					<strong style={{ borderBottom: "1px solid #C7C7C7", display: "block" }}>Session Events</strong>
					<div style={{ fontSize: "18px" }}>Summary of session details</div>
				</div>
				{renderByPages()}
			</div>
		);
	}

	function renderNotes(type: "session" | "roomScan") {
		const notes = type === "session" ? sessionNotes : roomScanNotes;
		const thumbnails = type === "session" ? noteThumbnails : roomScanNoteThumbnails;
		let includeTitle = true;
		let currentNoteIndex = 0;
		const renderByPages = () => {
			const res = [];
			while (currentNoteIndex < notes.length) {
				const heightLeft = PAGE_HEIGHT - PAGE_MARGIN * 2;
				let currentHeight = 0;
				if (includeTitle) currentHeight += ROW_HEIGHT + TITLE_HEIGHT; // title height
				let rows = 0;
				while (currentHeight + THUMBNAIL_HEIGHT < heightLeft && currentNoteIndex + rows < notes.length) {
					currentHeight += THUMBNAIL_HEIGHT;
					rows += 1;
				}
				const eventCluster = renderGenericNoteCluster(
					type,
					notes,
					thumbnails,
					includeTitle,
					currentNoteIndex,
					currentNoteIndex + rows,
					PAGE_MARGIN + heightLeft - currentHeight,
				);
				currentNoteIndex += rows;
				includeTitle = false;
				res.push(eventCluster);
			}
			return res;
		};

		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					borderRadius: "10px",
					minHeight: "150px",
					marginLeft: `40px`,
					marginRight: `40px`,
					marginTop: `${PAGE_MARGIN}px`,
				}}
			>
				<div style={{ height: TITLE_HEIGHT }}>
					{type === "session" ? (
						<>
							<strong style={{ borderBottom: "1px solid #C7C7C7", display: "block" }}>Session Notes</strong>
							<span style={{ marginTop: "5px", display: "block", fontSize: "18px" }}>View notes for this session</span>
						</>
					) : (
						<>
							<strong style={{ borderBottom: "1px solid #C7C7C7", display: "block" }}>Room Scan Notes</strong>
							<span style={{ marginTop: "5px", display: "block", fontSize: "18px" }}>
								View notes for the test-taker&#39;s room scan recording
							</span>
						</>
					)}
				</div>
				{renderByPages()}
			</div>
		);
	}

	function renderGenericNoteCluster(
		type: "session" | "roomScan",
		notes: SessionNote[],
		thumbnails: {
			time: number;
			url: string;
		}[],
		includeTitle: boolean,
		start: number,
		end: number,
		marginBottom: number,
	) {
		return (
			<div
				style={{
					borderRadius: "10px",
					border: "1px solid #ddd",
					marginBottom: `${marginBottom}px`,
					marginTop: `${includeTitle ? 0 : PAGE_MARGIN}px`,
				}}
			>
				<table
					style={{
						width: "100%",
						textAlign: "left",
						borderSpacing: "0px",
					}}
				>
					{includeTitle && (
						<thead>
							<tr style={{ height: `${ROW_HEIGHT}px`, textAlign: "center", fontSize: "18px" }}>
								<th style={{ padding: "8px", width: "10%" }}>Time</th>
								<th style={{ padding: "8px", width: "20%" }}>Thumbnail</th>
								<th style={{ padding: "8px", width: "70%" }}>Content</th>
							</tr>
						</thead>
					)}
					<tbody>
						{notes.slice(start, end).map((note, index) => {
							index += start;
							return (
								<tr key={index} style={{ height: `${ROW_HEIGHT}px` }}>
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											textAlign: "center",
											width: "10%",
											fontSize: "16px",
										}}
									>
										{formatEventTimestamp(
											Math.max(0, note.time * 1000 - (type === "session" ? props.timeOffset : 0)),
											props.duration,
										)}
									</td>
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											textAlign: "center",
											width: "20%",
										}}
									>
										<img
											style={{ width: "160px", height: "102px", borderRadius: "4px" }}
											src={thumbnails[index]?.url}
											alt=""
										></img>
									</td>
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											textAlign: "left",
											width: "70%",
											verticalAlign: "baseline",
										}}
									>
										<p style={{ textAlign: "left", fontSize: "14px", margin: "0px", marginLeft: "4px" }}>
											{" "}
											{note.content}{" "}
										</p>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	function renderKeypressDataCluster(includeTitle: boolean, start: number, end: number, marginBottom: number) {
		return (
			<div
				style={{
					borderRadius: "10px",
					border: "1px solid #ddd",
					marginBottom: `${marginBottom}px`,
					marginTop: `${includeTitle ? 0 : PAGE_MARGIN}px`,
				}}
			>
				<table
					style={{
						width: "100%",
						textAlign: "left",
						borderSpacing: "0px",
					}}
				>
					{includeTitle && (
						<thead>
							<tr style={{ height: `${ROW_HEIGHT}px`, textAlign: "center", fontSize: "18px" }}>
								<th style={{ padding: "8px", width: "10%" }}>Time</th>
								<th style={{ padding: "8px", width: "90%" }}>Content</th>
							</tr>
						</thead>
					)}
					<tbody>
						{keypressEvents.slice(start, end).map((event, index) => {
							index += start;
							return (
								<tr key={index} style={{ height: `${THUMBNAIL_HEIGHT}px` }}>
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											textAlign: "center",
											width: "10%",
											fontSize: "16px",
										}}
									>
										{formatEventTimestamp(
											Math.max(0, processKeypressTime(event.time, props.timeOffset)),
											props.duration,
										)}
									</td>
									<td
										style={{
											borderTop: index - start === 0 && !includeTitle ? undefined : "1px solid #ddd",
											padding: "8px",
											textAlign: "left",
											width: "60%",
											verticalAlign: "baseline",
										}}
									>
										<p style={{ fontSize: "14px" }}> {event.value} </p>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	function renderKeypressData() {
		let includeTitle = true;
		let currentNoteIndex = 0;
		const renderByPages = () => {
			const res = [];
			while (currentNoteIndex < keypressEvents.length) {
				const heightLeft = PAGE_HEIGHT - PAGE_MARGIN * 2;
				let currentHeight = 0;
				if (includeTitle) currentHeight += ROW_HEIGHT + TITLE_HEIGHT; // title height
				let rows = 0;
				while (currentHeight + THUMBNAIL_HEIGHT < heightLeft && currentNoteIndex + rows < keypressEvents.length) {
					currentHeight += THUMBNAIL_HEIGHT;
					rows += 1;
				}
				const eventCluster = renderKeypressDataCluster(
					includeTitle,
					currentNoteIndex,
					currentNoteIndex + rows,
					PAGE_MARGIN + heightLeft - currentHeight,
				);
				currentNoteIndex += rows;
				includeTitle = false;
				res.push(eventCluster);
			}
			return res;
		};

		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					borderRadius: "10px",
					minHeight: "150px",
					marginLeft: `40px`,
					marginRight: `40px`,
					marginTop: `${PAGE_MARGIN}px`,
				}}
			>
				<div style={{ height: TITLE_HEIGHT }}>
					<strong style={{ borderBottom: "1px solid #C7C7C7", display: "block" }}>Keystroke Log</strong>
					<span style={{ marginTop: "5px", display: "block", fontSize: "18px" }}>
						View all keystrokes entered by the test-taker.
					</span>
				</div>
				{renderByPages()}
			</div>
		);
	}

	function calculateNotePageCount(length: number) {
		let includeTitle = true;
		let currentNoteIndex = 0;
		let page = 0;
		while (currentNoteIndex < length) {
			const heightLeft = PAGE_HEIGHT - PAGE_MARGIN * 2;
			let currentHeight = 0;
			if (includeTitle) currentHeight += ROW_HEIGHT + TITLE_HEIGHT; // title height
			let rows = 0;
			while (currentHeight + THUMBNAIL_HEIGHT < heightLeft && currentNoteIndex + rows < length) {
				currentHeight += THUMBNAIL_HEIGHT;
				rows += 1;
			}

			currentNoteIndex += rows;
			includeTitle = false;
			page += 1;
		}
		return page;
	}

	function calculateEventsPageCount() {
		let includeTitle = true;
		let currentEventIndex = 0;
		let page = 0;
		while (currentEventIndex < sessionEvents.length) {
			const heightLeft = PAGE_HEIGHT - PAGE_MARGIN * 2;
			let currentHeight = 0;
			if (includeTitle) currentHeight += ROW_HEIGHT + TITLE_HEIGHT; // title height
			let rows = 0;
			while (currentEventIndex + rows < sessionEvents.length) {
				const rowHeight = isShowThumbnailEvent(sessionEvents[currentEventIndex + rows]) ? THUMBNAIL_HEIGHT : ROW_HEIGHT;
				if (currentHeight + rowHeight > heightLeft) {
					break;
				} else {
					rows += 1;
					currentHeight += rowHeight;
				}
			}
			currentEventIndex += rows;
			includeTitle = false;
			page += 1;
		}
		return page;
	}

	const photoType =
		props.signatureType !== null && !!props.idPhotoAvailable && !!props.personPhotoAvailable
			? "both"
			: props.signatureType !== null
				? "signature"
				: props.idPhotoAvailable && props.personPhotoAvailable
					? "id"
					: undefined;

	function ProctoringSessionDetails() {
		return (
			<div
				style={{
					flexDirection: "column",
					borderRadius: "10px",
					margin: "40px",
					marginTop: "0px",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<AssessmentSetting
					assessmentOption={
						sessionEvents.filter((event) => event.type == "session" && event.name === "start proctoring")?.[0]?.details
					}
				/>
			</div>
		);
	}

	return (
		<ThemeWrapper>
			<div id="component-to-pdf" style={{ margin: "0px", display: "grid" }}>
				<FirstPage
					assessmentOption={
						sessionEvents.filter((event) => event.type == "session" && event.name === "start proctoring")?.[0]?.details
					}
					eventPageCount={calculateEventsPageCount()}
					notePageCount={calculateNotePageCount(sessionNotes.length)}
					roomScanNoteCount={calculateNotePageCount(roomScanNotes.length)}
					studentName={props.userInfo?.full_name}
					numSummaryPages={photoType === "both" ? 2 : 1}
				/>
				<div style={{ height: "1187px" }}>
					<div>
						<PageHeader
							userInfo={props.userInfo ?? {}}
							circleRadius={props.circleRadius}
							severeCount={props.severeCount}
							severeDasharray={props.severeDasharray}
							severeOffset={props.severeOffset}
							majorCount={props.majorCount}
							majorDasharray={props.majorDasharray}
							majorOffset={props.majorOffset}
							minorCount={props.minorCount}
							minorDasharray={props.minorDasharray}
							minorOffset={props.minorOffset}
							maxFlagTxt={props.maxFlagTxt}
							sessionNum={props.sessionNum}
							totalSessions={props.totalSessions}
						/>
					</div>
					<div>
						{props.reportReady && photoType && (
							<div>
								<IdPersonSignaturePhoto
									uuid={props.uuid}
									setReportReady={props.setReportReady}
									type={photoType}
									signatureType={props.signatureType}
								/>
							</div>
						)}
					</div>
					{photoType !== "both" && <ProctoringSessionDetails />}
				</div>
				{photoType === "both" && (
					<div style={{ height: "1187px" }}>
						{" "}
						<ProctoringSessionDetails />
					</div>
				)}
				<div style={{ color: "#202124" }}>
					{renderEvents()}
					{!!sessionNotes.length && renderNotes("session")}
					{!!roomScanNotes.length && renderNotes("roomScan")}
					{!!keypressEvents.length && renderKeypressData()}
				</div>
			</div>
		</ThemeWrapper>
	);
};

const DownloadPDFReportButton = (props: ReportProps) => {
	return <PDFGenerator component={<Report {...props} />} />;
};

export default DownloadPDFReportButton;
